import React, {useEffect, useRef, useState} from "react";
import {useHistory} from "react-router-dom";
import Button from "@source/components/UI/buttons/button/button";
import SelectInput, {ISelectItem} from "@source/components/UI/inputs/selectInput/selectInput";
import Svg from "@source/components/UI/svg/svg";
import Notification from "@source/components/snackMessage/notification";
import ConsentController from "@source/api/controllers/consentController";
import {ConsentTemplate, ConsentTemplateStatus} from "@source/api/models/consentModels";
import {ConsentRequestState} from "@source/components/consentDocuments";
import {HOME_ROUTES} from "@source/utils/utils";
import {consentTemplateBuilderPath} from "@source/api/models/consentHelpers";
import iconDoc from "@source/assets/icons/iconDoc.svg";
import iconPlus from "@source/assets/icons/iconPlus.svg";
import styles from "./templateLibrary.module.scss";

export const NewTemplateChoice = ({organizationId, canReadTemplates}: {organizationId: string; canReadTemplates: boolean}) => {
  const history = useHistory();
  const [sources, setSources] = useState<ConsentTemplate[]>([]);
  const sourceId = new URLSearchParams(window.location.search).get("source") || "";
  const [selectedId, setSelectedId] = useState(sourceId);
  const [loading, setLoading] = useState(canReadTemplates);
  const [error, setError] = useState("");
  const [copying, setCopying] = useState(false);
  const requestVersion = useRef(0);
  const mounted = useRef(true);
  const selected = sources.find((template) => template._id === selectedId);
  const loadSources = async () => {
    if (!canReadTemplates) return;
    const version = ++requestVersion.current;
    setLoading(true); setError("");
    try {
      const activeSources = await ConsentController.listTemplates(
        organizationId,
        {status: ConsentTemplateStatus.ACTIVE, limit: 100, page: 0, sort: "desc"},
      );
      if (version !== requestVersion.current) return;
      if (!sourceId || activeSources.some((template) => template._id === sourceId)) {
        setSources(activeSources);
        return;
      }
      const requestedSource = await ConsentController.getTemplate(organizationId, sourceId);
      if (version !== requestVersion.current) return;
      setSources([...activeSources, requestedSource]);
    } catch (requestError: unknown) {
      if (version === requestVersion.current) setError(requestError instanceof Error ? requestError.message : "Unable to load templates.");
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  };
  useEffect(() => {
    if (!canReadTemplates) {
      setSources([]);
      setLoading(false);
      setError("");
      return;
    }
    setSelectedId(sourceId);
    loadSources();
    return () => { requestVersion.current += 1; };
  }, [organizationId, canReadTemplates, sourceId]);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const copySelected = async () => {
    if (!selected || copying) {
      if (copying) return;
      setError("Select an existing template first.");
      return;
    }
    const version = requestVersion.current;
    setCopying(true);
    setError("");
    try {
      const copied = await ConsentController.copyTemplate(organizationId, selected._id, {title: `${selected.title} (Copy)`});
      if (!mounted.current || version !== requestVersion.current) return;
      new Notification().success("Template copied successfully.");
      history.push(consentTemplateBuilderPath(copied._id));
    } catch (requestError: unknown) {
      if (mounted.current && version === requestVersion.current) setError(requestError instanceof Error ? requestError.message : "Unable to copy the template.");
    } finally {
      if (mounted.current && version === requestVersion.current) setCopying(false);
    }
  };
  return <div className={styles.choices}>
    <section className={styles.choice}>
      <span className={styles.choiceIcon} aria-hidden="true"><Svg src={iconPlus} color="#3956C7" width={16} height={16}/></span>
      <h3>Create from blank</h3>
      <p>Start a new template in the visual builder.</p>
      <Button event="link" link={HOME_ROUTES.ConsentDocumentsNewBlankTemplate}>Create from blank</Button>
    </section>
    <section className={styles.choice}>
      <span className={styles.choiceIcon} aria-hidden="true"><Svg src={iconDoc} color="#3956C7" width={16} height={16}/></span>
      <h3>Use an existing template</h3>
      <p>Copy an existing template, then edit the new copy.</p>
      {!canReadTemplates ? <ConsentRequestState variant="empty" title="Template selection unavailable" description="Your permission allows template management, but not reading templates. Ask an administrator for template viewing access."/> : null}
      {canReadTemplates && loading ? <ConsentRequestState variant="loading" title="Loading templates"/> : null}
      {canReadTemplates && error ? <ConsentRequestState variant="error" title="Unable to load templates" description={error} onRetry={loadSources}/> : null}
      {canReadTemplates && !loading && !error && !sources.length ? <ConsentRequestState variant="empty" title="No templates" description="There is no template available to copy."/> : null}
      {canReadTemplates && sources.length ? <>
        <SelectInput className={styles.sourceSelect} label="Source template" value={selectedId} placeholder="Select a template" items={[
          {id: "empty", value: "", title: "Select a template"},
          ...sources.map((template) => ({id: template._id, value: template._id, title: `${template.title} · v${template.version}${template.status === ConsentTemplateStatus.ARCHIVED ? " · Archived" : ""}`})),
        ]} handleChange={(event) => setSelectedId(event.target.value)}/>
        <Button event="button" disabled={copying || !selected} isLoading={copying} onClick={copySelected}>Use selected template</Button>
      </> : null}
    </section>
  </div>;
};
